
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import storage from "@/services/storage";

import UPDATE_WORK_SAMPLES from "@/apollo/profile/workSamples/UpdateWorkSamples.gql";
import GET_WORK_SAMPLES from "@/apollo/profile/workSamples/GetWorkSamples.gql";

import FormWrapper from "@/components/form/FormWrapper.vue";
import EnlargedImageViewer from "@/components/common/EnlargedImageViewer.vue";
import FormImageUpload from "@/components/tailwindui/form/FormImageUpload.vue";
import FormImageUploadPreview from "@/components/tailwindui/form/FormImageUploadPreview.vue";

import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";

interface Image {
  s3key: string;
  fileUrl: string;
}

@Component({
  components: {
    FormImageUpload,
    FormImageUploadPreview,
    EnlargedImageViewer,
    FormWrapper,
  },
  apollo: {
    workSamples: {
      query: GET_WORK_SAMPLES,
      variables() {
        return {
          id: this.userId,
        };
      },
      update: (data) => {
        return data.user_profile_by_pk.work_samples;
      },
    },
  },
})
export default class ProfileWorkSamples extends Vue {
  @Prop({ type: String, required: true }) readonly userId!: string;

  workSamples: string[] = [];

  images: Image[] = [];

  imagesLinks: string[] = [];

  index: number = 0;

  lightboxVisible: boolean = false;

  @Watch("workSamples")
  async watchLocalWorkSamplesValue(): Promise<void> {
    await this.fetchImages();
  }

  showImg (index: number): void {
    this.index = index;
    this.lightboxVisible = true;
  }
  
  removeFile(key: string): void {
    this.workSamples = this.workSamples.filter(k => k !== key);
    this.updateWorkSamples();
  }

  loadFile(key: string): void {
    this.workSamples = [...(this.workSamples ?? []), key];
    this.updateWorkSamples();
  }

  async fetchImages(): Promise<void> {
    const workSamples = this.workSamples ?? [];
    const imagePromises = workSamples.map(async sample => ({
      s3key: sample,
      fileUrl: await storage.get(sample),
    }));
  
    this.images = await Promise.all(imagePromises);
    this.imagesLinks = this.images.map(({ fileUrl }) => fileUrl);
  }

  async updateWorkSamples(): Promise<void> {
    try {
      await this.update();

      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage("Your work samples had been successfully updated.")
      );

      this.refetch();
    } catch (error) {
      this.$eventBus.emit(
        NOTIFICATION, 
        Message.errorMessage(error.message)
      );
    }
  }

  async update(): Promise<void> {
    const workSamples = `{${this.workSamples.join(",")}}`;

    await this.$apollo.mutate({
      mutation: UPDATE_WORK_SAMPLES,
      variables: {
        userId: this.userId,
        workSamples,
      },
    });
  }

  refetch(): void {
    this.$apollo.queries.workSamples.refetch();
    this.$emit("update");
  }
}
